import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { useToast } from '../components/ui/Toast';
import { DEFAULT_SORT } from '../components/referrals/ReferralTable';
import { fetchWorkflowCounts as apiFetchWorkflowCounts } from '../services/referralService';

const EMPTY_FILTER_OPTIONS = { clients: [], tpa_carriers: [], referral_types: [] };

/**
 * Custom hook encapsulating the state and data-fetching logic shared between
 * the open and closed referral list pages.
 *
 * @param {Function} fetchFn - Referral list fetch function (page-1 data).
 * @param {string} searchText - Current search text from the outer layout context.
 * @param {Object} [options]
 * @param {boolean} [options.applyDefaultSort=true]
 * @param {Function} [options.fetchOptionsFn] - Fetches the filter-options
 *   payload once on mount. Returns { clients, tpa_carriers, referral_types }.
 *   If omitted, filterOptions stays at the empty shape.
 */
function useReferralList(fetchFn, searchText, { applyDefaultSort = true, fetchOptionsFn, workflowStepFilter, assignedToFilter } = {}) {
  const [referrals, setReferrals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [workflowCounts, setWorkflowCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortColumns, setSortColumns] = useState([]);
  const [isDefaultSort, setIsDefaultSort] = useState(applyDefaultSort);
  const [openSortMenu, setOpenSortMenu] = useState(null);
  const [filters, setFilters] = useState({
    client_id: '',
    tpa_id: '',
    referral_type: '',
    status: '',
    assigned_to: '',
    search: '',
  });
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [reloadKey, setReloadKey] = useState(0);
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Patch a single referral row in local state. Used by ReferralDrawer to
  // reflect a just-posted comment without a full list refetch — the drawer
  // already knows the new status / reason / latest_comment from its own
  // activity-feed reload. No-op if the referral is not on the current page.
  const patchReferral = useCallback((referralId, patch) => {
    if (!referralId || !patch) return;
    setReferrals((prev) =>
      prev.map((r) => (r.referral_id === referralId ? { ...r, ...patch } : r)),
    );
  }, []);

  const handleResponse = useCallback((response) => {
    if (response.results) {
      setReferrals(response.results);
      setTotalCount(response.count ?? response.results.length);
      return true;
    } else if (Array.isArray(response)) {
      setReferrals(response);
      setTotalCount(response.length);
      return true;
    } else {
      logger.error('Unexpected response format:', Object.keys(response));
      toast.error('Unexpected response format from server');
      setError('Unexpected response format');
      return false;
    }
  }, [toast]);

  const buildOrderingParam = useCallback(() => {
    if (isDefaultSort) {
      return '-impact,due_date';
    }
    if (sortColumns.length === 0) {
      return undefined;
    }
    return sortColumns
      .map(({ field, direction }) => (direction === 'desc' ? `-${field}` : field))
      .join(',');
  }, [isDefaultSort, sortColumns]);

  // One-shot filter-options fetch — keyed only on fetchOptionsFn so the
  // dropdowns don't churn when filters/search change.
  useEffect(() => {
    if (!fetchOptionsFn) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const opts = await fetchOptionsFn();
        if (!cancelled) setFilterOptions(opts);
      } catch (err) {
        logger.error('Error loading referral filter options:', err);
        if (!cancelled) toastRef.current.error('Could not load filter options');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchOptionsFn]);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const params = { page: 1, page_size: 100 };
        if (searchText) params.search = searchText;
        if (workflowStepFilter) params.workflow_step = workflowStepFilter;
        if (assignedToFilter) {
          if (assignedToFilter === 'smartlight') params.assigned_to = 'smartlight';
          else if (assignedToFilter === 'tpa_carrier') params.assigned_to = 'tpa_carrier';
        }
        Object.entries(filters)
          .filter(([, v]) => v !== '')
          .forEach(([key, v]) => { params[key] = v; });
        const ordering = buildOrderingParam();
        if (ordering) params.ordering = ordering;

        const countsParams = {};
        if (searchText) countsParams.search = searchText;
        if (assignedToFilter) {
          if (assignedToFilter === 'smartlight') countsParams.assigned_to = 'smartlight';
          else if (assignedToFilter === 'tpa_carrier') countsParams.assigned_to = 'tpa_carrier';
        }
        Object.entries(filters)
          .filter(([, v]) => v !== '')
          .forEach(([key, v]) => { countsParams[key] = v; });

        const [response, countsResponse] = await Promise.all([
          fetchFn(params),
          apiFetchWorkflowCounts(countsParams).catch((err) => {
            logger.error('Error loading workflow counts:', err);
            return null;
          }),
        ]);
        handleResponse(response);
        if (countsResponse !== null) setWorkflowCounts(countsResponse);
      } catch (err) {
        logger.error('Error loading referrals:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [fetchFn, filters, sortColumns, isDefaultSort, searchText, buildOrderingParam, handleResponse, reloadKey, workflowStepFilter, assignedToFilter]);

  const handleSort = (columnKey, direction) => {
    setOpenSortMenu(null);

    const shouldPreserveDefault =
      isDefaultSort &&
      DEFAULT_SORT.some((s) => s.field === columnKey && s.direction === direction);

    const baseSorts = shouldPreserveDefault ? DEFAULT_SORT : sortColumns;
    const updated = [
      ...baseSorts.filter((s) => s.field !== columnKey),
      { field: columnKey, direction },
    ];

    const matchesDefault =
      updated.length === DEFAULT_SORT.length &&
      DEFAULT_SORT.every((ds) => updated.some((s) => s.field === ds.field && s.direction === ds.direction));

    if (matchesDefault) {
      setSortColumns([]);
      setIsDefaultSort(applyDefaultSort);
    } else {
      setSortColumns(updated);
      setIsDefaultSort(false);
    }
  };

  const handleClearColumnSort = (columnKey) => {
    if (isDefaultSort) {
      setSortColumns(DEFAULT_SORT.filter((s) => s.field !== columnKey));
      setIsDefaultSort(false);
    } else {
      const updated = sortColumns.filter((s) => s.field !== columnKey);
      if (updated.length === 0) {
        setIsDefaultSort(applyDefaultSort);
        setSortColumns([]);
      } else {
        setSortColumns(updated);
      }
    }
    setOpenSortMenu(null);
  };

  return {
    referrals,
    totalCount,
    loading,
    error,
    sortColumns,
    setSortColumns,
    isDefaultSort,
    setIsDefaultSort,
    openSortMenu,
    setOpenSortMenu,
    filters,
    setFilters,
    filterOptions,
    handleSort,
    handleClearColumnSort,
    reload,
    patchReferral,
    workflowCounts,
  };
}

export { useReferralList };
